import { z } from 'zod';
import { uuid, isoDateTime } from './common.js';
/** Milestone 6: Dinero invoicing, docs/design/05-dinero-mapping.md. */
/** Account mapping keys (section 4); values are Dinero account numbers from the accountant. */
export const ACCOUNT_KEYS = ['sales_dk', 'sales_eu_reverse_charge', 'sales_export', 'shipping', 'handling', 'discount', 'gift_card_liability', 'bambora_clearing', 'bank'];
export const ACCOUNT_LABELS = {
    sales_dk: 'Salg Danmark (25 % moms)',
    sales_eu_reverse_charge: 'Salg EU, omvendt betalingspligt',
    sales_export: 'Salg uden for EU (eksport)',
    shipping: 'Fragt',
    handling: 'Ekspeditionsgebyr',
    discount: 'Rabat',
    gift_card_liability: 'Gavekort (gæld)',
    bambora_clearing: 'Bambora mellemregning (indbetalingskonto)',
    bank: 'Bank',
};
export const invoiceKind = z.enum(['order', 'shop', 'credit']);
export const financeInvoiceStatus = z.enum(['pending', 'approved', 'created', 'booked', 'sent', 'paid', 'failed', 'cancelled']);
export const invoiceLine = z.object({ description: z.string(), quantity: z.number(), unitMinor: z.number().int(), account: z.string(), accountNumber: z.number().int().nullable(), discountPct: z.number().optional() });
export const invoiceDto = z.object({
    id: uuid,
    kind: invoiceKind,
    orderId: uuid.nullable(),
    orderNumber: z.string().nullable(),
    shopId: uuid.nullable(),
    shopName: z.string().nullable(),
    returnId: uuid.nullable(),
    customerId: uuid.nullable(),
    customerName: z.string().nullable(),
    status: financeInvoiceStatus,
    mode: z.enum(['dry_run', 'live']),
    currency: z.string(),
    date: z.string(),
    description: z.string(),
    comment: z.string().nullable(),
    lines: z.array(invoiceLine),
    totalMinor: z.number().int(),
    inclVat: z.boolean(),
    sendMethod: z.string().nullable(),
    dineroNumber: z.number().int().nullable(),
    dineroTotalMinor: z.number().int().nullable(),
    paidMinor: z.number().int(),
    error: z.string().nullable(),
    approvedBy: z.string().nullable(),
    bookedAt: isoDateTime.nullable(),
    sentAt: isoDateTime.nullable(),
    paidAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
});
export const invoiceQuery = z.object({
    status: financeInvoiceStatus.optional(),
    kind: invoiceKind.optional(),
    orderId: uuid.optional(),
    shopId: uuid.optional(),
    q: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
export const financeCustomerDto = z.object({
    id: uuid,
    number: z.string(),
    name: z.string(),
    isPerson: z.boolean(),
    attPerson: z.string().nullable(),
    street: z.string().nullable(),
    zip: z.string().nullable(),
    city: z.string().nullable(),
    countryCode: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    vatNumber: z.string().nullable(),
    eanNumber: z.string().nullable(),
    paymentDays: z.number().int().nullable(),
    invoiceMethod: z.enum(['email', 'ean', 'vat']),
    source: z.string(),
    dineroContactGuid: z.string().nullable(),
    shopCount: z.number().int(),
});
export const saveFinanceCustomerRequest = z.object({
    number: z.string().trim().min(1).max(30),
    name: z.string().trim().min(1).max(200),
    isPerson: z.boolean().default(false),
    attPerson: z.string().trim().max(200).nullable().optional(),
    street: z.string().trim().max(200).nullable().optional(),
    zip: z.string().trim().max(20).nullable().optional(),
    city: z.string().trim().max(100).nullable().optional(),
    countryCode: z.string().trim().length(2).default('DK'),
    email: z.string().trim().max(200).nullable().optional(),
    phone: z.string().trim().max(50).nullable().optional(),
    vatNumber: z.string().trim().max(30).nullable().optional(),
    eanNumber: z.string().trim().max(13).nullable().optional(),
    paymentDays: z.number().int().min(0).max(120).nullable().optional(),
    invoiceMethod: z.enum(['email', 'ean', 'vat']).default('email'),
});
/** The finance work lists (section 7). */
export const financeOverviewDto = z.object({
    connection: z.object({ mode: z.enum(['dry_run', 'live']), enabled: z.boolean(), connected: z.boolean(), organizationName: z.string().nullable(), lastSuccessAt: isoDateTime.nullable(), lastError: z.string().nullable(), missingAccounts: z.array(z.string()), startDate: z.string().nullable(), manualApproval: z.boolean() }),
    counts: z.object({ pending: z.number().int(), failed: z.number().int(), notInvoiced: z.number().int(), shopInvoicesDue: z.number().int(), creditsDue: z.number().int(), mismatches: z.number().int(), unpaidOverdue: z.number().int() }),
    notInvoiced: z.array(z.object({ orderId: uuid, orderNumber: z.string(), shopName: z.string().nullable(), provider: z.string(), shippedAt: isoDateTime.nullable(), totalMinor: z.number().int(), reason: z.string() })),
    shopInvoicesDue: z.array(z.object({ shopId: uuid, shopName: z.string(), customerNumber: z.string().nullable(), orders: z.number().int(), status: z.string() })),
});
export const approveInvoicesRequest = z.object({ ids: z.array(uuid).min(1).max(500) });
export const shopInvoiceRequest = z.object({ shopId: uuid });
//# sourceMappingURL=finance.js.map